import { z } from 'zod';
import { SUPPORTED_LOCALES } from '../constants/app.js';
import { idSchema, isoDateTimeSchema } from './common.js';
import { supportedLocaleSchema } from './plm-material.js';
export const TRANSLATION_ORIGINS = ['MANUAL', 'MACHINE', 'MEMORY'];
export const TRANSLATION_REVIEW_STATUSES = ['REVIEW_REQUIRED', 'REVIEWED'];
export const translationOriginSchema = z.enum(TRANSLATION_ORIGINS);
export const translationReviewStatusSchema = z.enum(TRANSLATION_REVIEW_STATUSES);
/** Optional on writes for backward compatibility; API reads always materialize every field. */
export const translationMetadataWriteSchema = z
    .object({
    origin: translationOriginSchema.optional(),
    sourceLocale: supportedLocaleSchema.nullish(),
    model: z.string().trim().max(120).nullish(),
    generatedAt: isoDateTimeSchema.nullish(),
    generatedByUserId: idSchema.nullish(),
    reviewStatus: translationReviewStatusSchema.optional(),
    reviewedAt: isoDateTimeSchema.nullish(),
    reviewedByUserId: idSchema.nullish(),
    sourceTextHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .nullish(),
})
    .strict();
export const translationMetadataSchema = z.object({
    origin: translationOriginSchema,
    sourceLocale: supportedLocaleSchema.nullable(),
    model: z.string().max(120).nullable(),
    generatedAt: isoDateTimeSchema.nullable(),
    generatedByUserId: idSchema.nullable(),
    reviewStatus: translationReviewStatusSchema,
    reviewedAt: isoDateTimeSchema.nullable(),
    reviewedByUserId: idSchema.nullable(),
    sourceTextHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .nullable(),
});
export const translationSourceOptionSchema = z
    .object({
    id: idSchema.optional(),
    value: z.string().trim().min(1).max(120),
    label: z.string().trim().min(1).max(240),
})
    .strict();
export const translationExistingTargetSchema = z
    .object({
    targetLocale: supportedLocaleSchema,
    label: z.string().max(240).nullish(),
    description: z.string().max(2_000).nullish(),
    helpText: z.string().max(2_000).nullish(),
    options: z
        .array(translationSourceOptionSchema.extend({ label: z.string().trim().max(240) }).strict())
        .max(100)
        .default([]),
})
    .strict();
export const translationGlossaryHintSchema = z
    .object({
    sourceTerm: z.string().trim().min(1).max(240),
    sourceLocale: supportedLocaleSchema,
    targetLocale: supportedLocaleSchema,
    targetTerm: z.string().trim().min(1).max(240),
    protected: z.boolean().default(false),
    caseSensitive: z.boolean().default(false),
})
    .strict();
export const translationGenerateInputSchema = z
    .object({
    sourceLocale: supportedLocaleSchema,
    targetLocales: z.array(supportedLocaleSchema).min(1).max(SUPPORTED_LOCALES.length),
    context: z.string().trim().min(1).max(500),
    label: z.string().trim().max(240).nullish(),
    description: z.string().max(2_000).nullish(),
    helpText: z.string().max(2_000).nullish(),
    options: z.array(translationSourceOptionSchema).max(100).default([]),
    existingTranslations: z.array(translationExistingTargetSchema).max(SUPPORTED_LOCALES.length),
    glossary: z.array(translationGlossaryHintSchema).max(500).default([]),
    allowHtml: z.boolean().default(false),
})
    .strict()
    .superRefine((value, ctx) => {
    const noLabel = value.label === undefined || value.label === null || value.label.trim() === '';
    const noDescription = value.description === undefined ||
        value.description === null ||
        value.description.trim() === '';
    const noHelpText = value.helpText === undefined || value.helpText === null || value.helpText.trim() === '';
    if (noLabel && noDescription && noHelpText && value.options.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['label'],
            message: 'Mindestens ein sichtbarer Definitionstext ist erforderlich.',
        });
    }
    const uniqueTargets = new Set(value.targetLocales);
    if (uniqueTargets.size !== value.targetLocales.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetLocales'],
            message: 'Eine Zielsprache ist doppelt angegeben.',
        });
    }
    if (uniqueTargets.has(value.sourceLocale)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetLocales'],
            message: 'Quell- und Zielsprache müssen verschieden sein.',
        });
    }
});
export const translatedOptionSchema = z
    .object({
    id: idSchema.optional(),
    value: z.string().min(1).max(120),
    label: z.string().trim().min(1).max(240),
})
    .strict();
export const translationSuggestionSchema = z
    .object({
    targetLocale: supportedLocaleSchema,
    label: z.string().trim().max(240).nullable(),
    description: z.string().max(2_000).nullable(),
    helpText: z.string().max(2_000).nullable(),
    options: z.array(translatedOptionSchema).max(100),
    warnings: z.array(z.string().trim().min(1).max(500)).max(20),
    model: z.string().trim().min(1).max(120),
    generatedAt: isoDateTimeSchema,
    generatedByUserId: idSchema,
    origin: z.enum(['MACHINE', 'MEMORY']),
    sourceLocale: supportedLocaleSchema,
    reviewStatus: z.literal('REVIEW_REQUIRED'),
    sourceTextHash: z.string().regex(/^[a-f0-9]{64}$/u),
})
    .strict();
export const translationGenerateResponseSchema = z
    .object({ data: z.array(translationSuggestionSchema).min(1).max(SUPPORTED_LOCALES.length) })
    .strict();
export const TRANSLATION_RESOURCE_KINDS = ['DEFINITION', 'GROUP'];
export const TRANSLATION_BATCH_STATUSES = [
    'QUEUED',
    'PROCESSING',
    'PAUSED',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
];
export const translationResourceKindSchema = z.enum(TRANSLATION_RESOURCE_KINDS);
export const translationBatchStatusSchema = z.enum(TRANSLATION_BATCH_STATUSES);
export const translationCompletenessSchema = z.object({
    locale: supportedLocaleSchema,
    complete: z.boolean(),
    missingPaths: z.array(z.string().min(1).max(300)),
    reviewRequiredCount: z.number().int().min(0),
    staleCount: z.number().int().min(0),
});
export const customFieldTranslationOverviewItemSchema = z.object({
    kind: translationResourceKindSchema,
    id: idSchema,
    key: z.string(),
    name: z.string(),
    entityTypes: z.array(z.string()),
    categoryId: idSchema.nullable(),
    status: z.enum(['DRAFT', 'IN_REVIEW', 'ACTIVE', 'ARCHIVED']).nullable(),
    completeness: z.array(translationCompletenessSchema).length(SUPPORTED_LOCALES.length),
    href: z.string().startsWith('/'),
});
export const customFieldTranslationOverviewQuerySchema = z.object({
    missingOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
    reviewRequiredOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
    staleOnly: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
    entityType: z.string().trim().max(60).optional(),
    definitionIds: z.array(idSchema).max(500).optional(),
    categoryId: idSchema.optional(),
    status: z.enum(['DRAFT', 'IN_REVIEW', 'ACTIVE', 'ARCHIVED']).optional(),
    locale: supportedLocaleSchema.optional(),
});
export const customFieldTranslationOverviewResponseSchema = z.object({
    data: z.array(customFieldTranslationOverviewItemSchema),
    totals: z.object({
        resources: z.number().int().min(0),
        missing: z.number().int().min(0),
        reviewRequired: z.number().int().min(0),
        stale: z.number().int().min(0),
        byLocale: z.record(supportedLocaleSchema, z.object({ complete: z.number().int().min(0), total: z.number().int().min(0) })),
    }),
});
export const translationBatchCreateSchema = z
    .object({
    idempotencyKey: z.string().trim().min(8).max(120),
    sourceLocale: supportedLocaleSchema,
    targetLocales: z.array(supportedLocaleSchema).min(1).max(SUPPORTED_LOCALES.length),
    filters: customFieldTranslationOverviewQuerySchema.default({}),
})
    .strict()
    .refine((value) => !value.targetLocales.includes(value.sourceLocale), {
    path: ['targetLocales'],
    message: 'Die Quellsprache darf nicht zugleich Zielsprache sein.',
});
export const translationBatchErrorSchema = z.object({
    resourceKind: translationResourceKindSchema,
    resourceId: idSchema,
    locale: supportedLocaleSchema,
    message: z.string().max(500),
    trackingId: z.string().max(80).nullable(),
});
export const translationBatchJobSchema = z.object({
    id: idSchema,
    status: translationBatchStatusSchema,
    sourceLocale: supportedLocaleSchema,
    targetLocales: z.array(supportedLocaleSchema),
    progressTotal: z.number().int().min(0),
    progressCompleted: z.number().int().min(0),
    progressFailed: z.number().int().min(0),
    errors: z.array(translationBatchErrorSchema),
    failureMessage: z.string().max(500).nullable(),
    failureTrackingId: z.string().max(80).nullable(),
    cancelRequested: z.boolean(),
    attempts: z.number().int().min(0),
    createdAt: isoDateTimeSchema,
    startedAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
});
export const translationReviewInputSchema = z
    .object({
    resources: z
        .array(z
        .object({
        kind: translationResourceKindSchema,
        id: idSchema,
        locales: z.array(supportedLocaleSchema).min(1).max(SUPPORTED_LOCALES.length),
    })
        .strict())
        .min(1)
        .max(500),
})
    .strict();
export const translationReviewResponseSchema = z.object({
    reviewed: z.number().int().min(0),
});
export const translationGlossaryTranslationSchema = z
    .object({
    targetLocale: supportedLocaleSchema,
    targetTerm: z.string().trim().min(1).max(240),
})
    .strict();
export const translationGlossaryWriteSchema = z
    .object({
    sourceTerm: z.string().trim().min(1).max(240),
    sourceLocale: supportedLocaleSchema,
    protected: z.boolean().default(false),
    caseSensitive: z.boolean().default(false),
    translations: z.array(translationGlossaryTranslationSchema).max(SUPPORTED_LOCALES.length - 1),
})
    .strict()
    .superRefine((value, ctx) => {
    const expected = SUPPORTED_LOCALES.filter((locale) => locale !== value.sourceLocale);
    const seen = new Set();
    for (const [index, translation] of value.translations.entries()) {
        if (translation.targetLocale === value.sourceLocale) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['translations', index, 'targetLocale'],
                message: 'Die Quellsprache darf keine Zielübersetzung sein.',
            });
        }
        if (seen.has(translation.targetLocale)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['translations', index, 'targetLocale'],
                message: 'Die Zielsprache ist doppelt vorhanden.',
            });
        }
        seen.add(translation.targetLocale);
        if (value.protected && translation.targetTerm !== value.sourceTerm) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['translations', index, 'targetTerm'],
                message: 'Ein geschützter Begriff muss unverändert bleiben.',
            });
        }
    }
    for (const locale of expected) {
        if (seen.has(locale))
            continue;
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['translations', locale, 'targetTerm'],
            message: `Die Glossarübersetzung für ${locale} fehlt.`,
        });
    }
});
export const translationGlossaryUpdateSchema = translationGlossaryWriteSchema;
export const translationGlossaryEntrySchema = z.object({
    id: idSchema,
    sourceTerm: z.string(),
    sourceLocale: supportedLocaleSchema,
    protected: z.boolean(),
    caseSensitive: z.boolean(),
    archivedAt: isoDateTimeSchema.nullable(),
    translations: z.array(translationGlossaryTranslationSchema),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const translationGlossaryListQuerySchema = z.object({
    sourceLocale: supportedLocaleSchema.optional(),
    targetLocale: supportedLocaleSchema.optional(),
    search: z.string().trim().max(240).optional(),
    includeArchived: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .default(false)
        .transform((value) => value === true || value === 'true'),
});
export const translationGlossaryListResponseSchema = z.object({
    data: z.array(translationGlossaryEntrySchema),
});
//# sourceMappingURL=translation.js.map